import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, useParams } from "react-router";
import { AuthenticateWithRedirectCallback } from "@clerk/clerk-react";
import { RouteErrorFallback } from "./components/RouteErrorFallback";
import {
  RedirectToHubGuides,
  RedirectToHubNews,
  RedirectToHubResources,
  RedirectToHubUniSport,
  RedirectToHubUniSportRankings,
} from "./components/studenthub/HubRedirect";
// ─── Retry wrapper for lazy imports ─────────────────────────
// Recovers from transient network failures with 3 retries.
// On final failure (e.g. stale Vite port in browser cache), forces a hard
// reload so the page picks up fresh chunk URLs from the current dev server.
function lazyRetry<T extends { default: React.ComponentType<any> }>(
  factory: () => Promise<T>,
  retries = 3,
): React.LazyExoticComponent<T["default"]> {
  return lazy(() => {
    const attempt = (remaining: number): Promise<T> =>
      factory().catch((err) => {
        if (remaining <= 0) {
          // Guard against infinite reload loops — only reload once per 15 s
          if (typeof window !== "undefined") {
            const KEY = "chunk_reload_at";
            const last = parseInt(sessionStorage.getItem(KEY) ?? "0", 10);
            if (Date.now() - last > 15_000) {
              sessionStorage.setItem(KEY, String(Date.now()));
              window.location.reload();
            }
          }
          return new Promise<T>(() => {}); // never resolves; reload takes over
        }
        return new Promise<T>((resolve) =>
          setTimeout(() => resolve(attempt(remaining - 1)), 1000),
        );
      });
    return attempt(retries);
  });
}

// ─── Student Hub Pages ───────────────────────────────────────
const HubHome              = lazyRetry(() => import("./pages/studenthub/Home").then(m => ({ default: m.Home })));
const HubUniversities      = lazyRetry(() => import("./pages/studenthub/Universities").then(m => ({ default: m.Universities })));
const HubUniversityDetail  = lazyRetry(() => import("./pages/studenthub/UniversityDetail").then(m => ({ default: m.UniversityDetail })));
const HubResourcesHub      = lazyRetry(() => import("./pages/studenthub/ResourcesHub").then(m => ({ default: m.ResourcesHub })));
const HubNews              = lazyRetry(() => import("./pages/studenthub/News").then(m => ({ default: m.News })));
const HubAppWrapper        = lazyRetry(() => import("./components/studenthub/AppWrapper").then(m => ({ default: m.AppWrapper })));
const HubUniSport          = lazyRetry(() => import("./pages/studenthub/UniSport").then(m => ({ default: m.default })));
const HubBucsRankings      = lazyRetry(() => import("./pages/studenthub/BucsRankings").then(m => ({ default: m.default })));
const HubBournemouthUni    = lazyRetry(() => import("./pages/studenthub/BournemouthUniversity").then(m => ({ default: m.BournemouthUniversity })));
const ReportBug            = lazyRetry(() => import("./pages/studenthub/ReportBug").then(m => ({ default: m.ReportBug })));
const PrivacyPolicy        = lazyRetry(() => import("./pages/studenthub/PrivacyPolicy").then(m => ({ default: m.PrivacyPolicy })));
const Guides               = lazyRetry(() => import("./pages/studenthub/Guides").then(m => ({ default: m.Guides })));
const NotFound             = lazyRetry(() => import("./pages/studenthub/NotFound").then(m => ({ default: m.NotFound })));
const HubLogin             = lazyRetry(() => import("./pages/studenthub/Login").then(m => ({ default: m.Login })));
const HubSaved             = lazyRetry(() => import("./pages/studenthub/Saved").then(m => ({ default: m.Saved })));
const HubProfile           = lazyRetry(() => import("./pages/studenthub/Profile").then(m => ({ default: m.Profile })));
const HubVerifyEmail       = lazyRetry(() => import("./pages/studenthub/VerifyEmail").then(m => ({ default: m.VerifyEmail })));

function RouteFallback() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero placeholder */}
      <div className="bg-white border-b border-gray-100 px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-3xl mx-auto space-y-4">
          <div className="h-6 w-32 bg-gray-100 rounded-full mx-auto animate-pulse" />
          <div className="h-12 w-full bg-gray-100 rounded-2xl animate-pulse" />
          <div className="h-12 w-3/4 mx-auto bg-gray-100 rounded-2xl animate-pulse" />
          <div className="h-5 w-2/3 mx-auto bg-gray-100 rounded-full animate-pulse" />
        </div>
      </div>
      {/* Content placeholder */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-32 bg-gray-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      </div>
    </div>
  );
}

function routeElement(Component: React.ComponentType) {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Component />
    </Suspense>
  );
}

const BOURNEMOUTH_UNIVERSITY_PATH = "/hub/universities/bournemouth-university";
const BOURNEMOUTH_UNIVERSITY_ALIASES = new Set([
  "bournemouth-university",
  "b50",
  "bour",
  "22e93c90-ca5f-983e-8c2a-f215d4e544d3",
  "6c4c2877-cb7e-462a-b910-061de55b1ea7",
]);

function UniversityDetailRoute() {
  const { id, section } = useParams();
  if (id && BOURNEMOUTH_UNIVERSITY_ALIASES.has(id.toLowerCase())) {
    return <Navigate to={section ? `${BOURNEMOUTH_UNIVERSITY_PATH}/${section}` : BOURNEMOUTH_UNIVERSITY_PATH} replace />;
  }
  return routeElement(HubUniversityDetail);
}

// ─── Router ──────────────────────────────────────────────────
export const router = createBrowserRouter([
  {
    path: "/",
    errorElement: <RouteErrorFallback />,
    children: [
      // Root → Student Hub
      { index: true, element: <Navigate to="/hub" replace /> },

      // ── Student Hub ───────────────────────────────────────
      {
        path: "hub",
        errorElement: <RouteErrorFallback />,
        children: [
          {
            element: routeElement(HubAppWrapper),
            children: [
              { index: true,                    element: routeElement(HubHome)          },
              { path: "universities",           element: routeElement(HubUniversities)  },
              { path: "universities/bournemouth-university",          element: routeElement(HubBournemouthUni) },
              { path: "universities/bournemouth-university/:section", element: routeElement(HubBournemouthUni) },
              { path: "universities/:id",          element: <UniversityDetailRoute /> },
              { path: "universities/:id/:section", element: <UniversityDetailRoute /> },
              { path: "resources",              element: routeElement(HubResourcesHub)  },
              { path: "resources/:category",    element: routeElement(HubResourcesHub)  },
              { path: "news",                   element: routeElement(HubNews)          },
              { path: "news/:section",          element: routeElement(HubNews)          },
              { path: "uni-sport",                  element: routeElement(HubUniSport)        },
              { path: "uni-sport/rankings",         element: routeElement(HubBucsRankings)    },
              { path: "uni-sport/:sportId",         element: routeElement(HubUniSport)        },
              { path: "uni-sport/:sportId/:tab",    element: routeElement(HubUniSport)        },
              { path: "guides",                          element: routeElement(Guides)            },
              { path: "guides/:topicSlug",               element: routeElement(Guides)            },
              { path: "guides/:topicSlug/:journeySlug",  element: routeElement(Guides)            },
              { path: "apply",                  element: <Navigate to="/hub/resources?view=tools" replace /> },
              { path: "uni-matcher",            element: <Navigate to="/hub" replace /> },
              { path: "course-matcher",         element: <Navigate to="/hub" replace /> },
              { path: "saved",                  element: routeElement(HubSaved)         },
              { path: "profile",                element: routeElement(HubProfile)       },
              { path: "verify-email",           element: routeElement(HubVerifyEmail)   },
              { path: "*",                      element: routeElement(NotFound)         },
            ],
          },
        ],
      },

      // OAuth callback — Clerk completes the session here after Google redirect
      { path: "sso-callback", element: <AuthenticateWithRedirectCallback /> },

      // Public standalone pages (no hub shell)
      { path: "report-bug",     element: routeElement(ReportBug)     },
      { path: "privacy-policy",  element: routeElement(PrivacyPolicy) },
      { path: "login",           element: routeElement(HubLogin)      },

      // Short-path aliases (bookmark-friendly without /hub prefix)
      { path: "news",              element: <RedirectToHubNews /> },
      { path: "news/:section",     element: <RedirectToHubNews /> },
      { path: "resources",         element: <RedirectToHubResources /> },
      { path: "resources/:category", element: <RedirectToHubResources /> },
      { path: "guides",            element: <Navigate to="/hub/guides" replace /> },
      { path: "guides/:topicSlug", element: <Navigate to="/hub/guides" replace /> },
      { path: "guides/:topicSlug/:journeySlug", element: <Navigate to="/hub/guides" replace /> },
      { path: "uni-sport",               element: <RedirectToHubUniSport /> },
      { path: "uni-sport/rankings",      element: <RedirectToHubUniSportRankings /> },
      { path: "uni-sport/:sportId",      element: <RedirectToHubUniSport /> },
      { path: "uni-sport/:sportId/:tab", element: <RedirectToHubUniSport /> },

      // Legacy redirects
      { path: "universities/:id", element: <Navigate to="/hub/universities" replace /> },
      { path: "courses/:id",      element: <Navigate to="/hub" replace /> },

      { path: "*", element: routeElement(NotFound) },
    ],
  },
]);
